import { Stack } from '@mui/material';
import React from 'react';

import { colors } from '../../../components';
import SummaryCard from '../../../components/ui-components/SummaryCard';
import { ContractApiResponse } from '../../../services/contract-services';
import { formatCurrency, formatToUSDate, getCurrentPaymentDueInfo } from '../../../utils/helper';
import { useTranslation } from '../../../utils/translations';

interface ContractSummaryCardsProps {
    contract: ContractApiResponse;
}

export const ContractSummaryCards: React.FC<ContractSummaryCardsProps> = ({ contract }) => {
    const { t } = useTranslation();
    const currentPaymentDueInfo = getCurrentPaymentDueInfo(contract);

    return (
        <Stack
            direction={{ xs: 'row', sm: 'row' }}
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            gap={2}
            overflow={{ xs: 'visible', sm: 'auto' }}
            sx={{
                '& .summaryCard': {
                    alignItems: 'end',
                },
            }}
        >
            <Stack minWidth={{ xs: 140, sm: 269 }} width={{ xs: 'calc(50% - 8px)', sm: '100%' }}>
                <SummaryCard
                    title={t('contracts.details.monthlyPayment')}
                    value={formatCurrency(contract.EMIAmount)}
                    icon="icon-dollar"
                    iconBgColor={colors.fullWhite}
                    iconPadding={0}
                    iconColor={colors.greyPreset[80]}
                />
            </Stack>
            <Stack minWidth={{ xs: 140, sm: 269 }} width={{ xs: 'calc(50% - 8px)', sm: '100%' }}>
                <SummaryCard
                    title={
                        currentPaymentDueInfo.isCurrentDueActual
                            ? t('contracts.details.currentPaymentDue')
                            : t('contracts.details.nextPaymentDue')
                    }
                    value={formatCurrency(currentPaymentDueInfo.amount)}
                    icon="icon-dollar"
                    iconBgColor={colors.fullWhite}
                    iconPadding={0}
                    iconColor={colors.greyPreset[80]}
                    titleColor={currentPaymentDueInfo.isCurrentDueActual ? undefined : colors.primaryLight[100]}
                />
            </Stack>
            {Number(contract.PastDue) > 0 && (
                <Stack minWidth={{ xs: 140, sm: 269 }} width={{ xs: 'calc(50% - 8px)', sm: '100%' }}>
                    <SummaryCard
                        title={t('contracts.details.lastPaymentDate')}
                        value={contract.LastPaymentDate ? formatToUSDate(contract.LastPaymentDate) : 'N/A'}
                        icon="icon-calendar-fill"
                        iconBgColor={colors.fullWhite}
                        iconPadding={0}
                        iconColor={colors.greyPreset[80]}
                    />
                </Stack>
            )}
            {Number(contract.PastDue) > 0 && (
                <Stack minWidth={{ xs: 140, sm: 269 }} width={{ xs: 'calc(50% - 8px)', sm: '100%' }}>
                    <SummaryCard
                        title={t('contracts.details.totalAmountDue') || 'Total Amount Due'}
                        value={formatCurrency(Number(contract.PastDue) + Number(contract.CurrentDue))}
                        icon="icon-dollar"
                        iconBgColor={colors.fullWhite}
                        iconPadding={0}
                        iconColor={colors.greyPreset[80]}
                        statusChipProps={{
                            label: t('contracts.details.overdue') || 'Overdue',
                            chipBgColor: colors.errorPreset[10],
                        }}
                        infoText={t('contracts.details.theAmountIncludesAllPastDueAmountsPlusAnyAdditionalFeesCharges')}
                    />
                </Stack>
            )}
        </Stack>
    );
};
